import { normalizeTemplateFields as normalizeStudioTemplateFields } from '@/utils/templateVariables';

export interface StoredTemplateOverride<TCustom extends Record<string, unknown>> {
  templateRef: string;
  customData: TCustom & { deleted?: boolean; ['__deleted']?: boolean };
}

export interface TemplateWithRef {
  id: string;
  name: string;
  templateRef: string;
}

const normalizeStoredTemplate = <T extends TemplateWithRef>(template: T): T => {
  return normalizeStudioTemplateFields(template as T & Record<string, unknown>) as T;
};

const isDeletedOverride = (customData: Record<string, unknown>) => {
  const legacyDeleted = Reflect.get(customData, '__deleted');
  return Boolean(customData.deleted || legacyDeleted);
};

const parseOverrides = <TCustom extends Record<string, unknown>>(
  raw: string | null
): StoredTemplateOverride<TCustom>[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as StoredTemplateOverride<TCustom>[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(item => item && typeof item.templateRef === 'string' && item.customData);
  } catch {
    return [];
  }
};

export const loadTemplatesWithDiffs = <T extends TemplateWithRef>(
  storageKey: string,
  defaults: T[]
): T[] => {
  const raw = localStorage.getItem(storageKey);
  const overrides = parseOverrides<Record<string, unknown>>(raw);
  if (overrides.length === 0) return defaults;

  const defaultMap = new Map(defaults.map(template => [template.templateRef, template]));
  const overrideMap = new Map(
    overrides.map(override => [override.templateRef, override.customData])
  );

  const resolvedDefaults = defaults
    .map(template => {
      const customData = overrideMap.get(template.templateRef);
      if (!customData) return template;
      const deleted = isDeletedOverride(customData);
      if (deleted) return null;
      return normalizeStoredTemplate({
        ...template,
        ...customData,
        templateRef: template.templateRef,
      } as T);
    })
    .filter(Boolean) as T[];

  const customTemplates: T[] = overrides
    .filter(override => {
      const deleted = isDeletedOverride(override.customData);
      return !defaultMap.has(override.templateRef) && !deleted;
    })
    .map((override, index) => {
      const customData = override.customData as unknown as T;
      const fallbackId = `custom-${index + 1}`;
      const fallbackName = 'Custom Template';
      return {
        ...customData,
        id: String(customData.id || fallbackId),
        name: String(customData.name || fallbackName),
        templateRef: override.templateRef,
      } as T;
    });

  return [...resolvedDefaults, ...customTemplates].map(template =>
    normalizeStoredTemplate(template)
  );
};

export const resolveTemplatesWithServerFallback = <T extends TemplateWithRef>(
  storageKey: string,
  defaults: T[],
  serverTemplates?: T[] | null
): T[] => {
  if (Array.isArray(serverTemplates)) {
    return serverTemplates.map(template => normalizeStoredTemplate(template));
  }

  return loadTemplatesWithDiffs(storageKey, defaults);
};

export const saveTemplateDiffs = <T extends TemplateWithRef>(
  storageKey: string,
  templates: T[],
  defaults: T[]
) => {
  const normalizedTemplates = templates.map(template => normalizeStoredTemplate(template));
  const normalizedDefaults = defaults.map(template => normalizeStoredTemplate(template));
  const defaultMap = new Map(normalizedDefaults.map(template => [template.templateRef, template]));
  const currentRefs = new Set(normalizedTemplates.map(template => template.templateRef));

  const overrides: StoredTemplateOverride<Record<string, unknown>>[] = [];

  normalizedTemplates.forEach(template => {
    const base = defaultMap.get(template.templateRef);
    const templateRecord = template as Record<string, unknown>;

    if (!base) {
      const { templateRef, ...customData } = template;
      overrides.push({ templateRef, customData });
      return;
    }

    const baseRecord = base as Record<string, unknown>;
    const diff: Record<string, unknown> = {};
    Object.keys(templateRecord).forEach(key => {
      if (key === 'templateRef') return;
      if (JSON.stringify(templateRecord[key]) !== JSON.stringify(baseRecord[key])) {
        diff[key] = templateRecord[key];
      }
    });

    if (Object.keys(diff).length > 0) {
      overrides.push({ templateRef: template.templateRef, customData: diff });
    }
  });

  normalizedDefaults.forEach(template => {
    if (!currentRefs.has(template.templateRef)) {
      overrides.push({
        templateRef: template.templateRef,
        customData: { deleted: true },
      });
    }
  });

  localStorage.setItem(storageKey, JSON.stringify(overrides));
};
